import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, INR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CombosAdmin from "@/components/CombosAdmin";
import {
  LogOut, Plus, Pencil, Trash2, Save, X, Image as ImageIcon,
  Settings as SettingsIcon, RefreshCw, Palette, Tag, KeyRound,
} from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  brand: "", name: "", price_5ml: "", price_10ml: "", stock_ml: "70", image_url: "",
};

const defaultSettings = {
  whatsapp_number: "",
  instagram_handle: "",
  upi_id: "",
  shipping_standard: 100,
  shipping_reduced: 50,
  shipping_threshold_reduced: 1000,
  shipping_threshold_free: 3000,
  site_name: "tpg.decants",
  hero_image_url: "",
  instagram_url: "",
  hero_eyebrow: "",
  hero_title: "",
  hero_subtitle: "",
  hero_cta_primary: "Explore Catalog",
  hero_cta_secondary: "Follow on Instagram",
  catalog_eyebrow: "The Catalog",
  catalog_title: "Every bottle, in a size that suits you.",
  catalog_empty_text: "No perfumes match your search.",
  footer_tagline: "",
  footer_visit_title: "Visit the Guy",
  footer_boutique_title: "The Boutique",
  footer_bullet_1: "100% authentic, hand-decanted",
  footer_bullet_2: "Pay via UPI — GPay / PhonePe",
  footer_bullet_3: "Orders confirmed via WhatsApp",
  footer_bullet_4: "Free shipping on orders ₹3000+",
  footer_marquee: "Crafted for the fragrance obsessed",
  about_title: "About Us",
  about_teaser: "A story behind every bottle.",
  about_body: "",
  promo_active: false,
  promo_code: "",
  promo_percent: 10,
  promo_min_subtotal: 1000,
};

export default function Admin() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [q, setQ] = useState("");
  const [settings, setSettings] = useState(defaultSettings);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingContent, setSavingContent] = useState(false);
  const [savingPromo, setSavingPromo] = useState(false);
  const [resetMl, setResetMl] = useState("70");
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [changingPw, setChangingPw] = useState(false);
  const [resetting, setResetting] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([api.get("/products"), api.get("/admin/settings")]);
      setProducts(p.data);
      setSettings({ ...defaultSettings, ...s.data });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setCreating(false);
    setForm({
      brand: p.brand,
      name: p.name,
      price_5ml: String(p.price_5ml),
      price_10ml: String(p.price_10ml),
      stock_ml: String(p.stock_ml ?? 0),
      image_url: p.image_url || "",
    });
  };

  const startCreate = () => {
    setCreating(true);
    setEditingId(null);
    setForm(emptyForm);
  };

  const cancel = () => {
    setCreating(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const save = async () => {
    const payload = {
      brand: form.brand.trim(),
      name: form.name.trim(),
      price_5ml: parseInt(form.price_5ml, 10),
      price_10ml: parseInt(form.price_10ml, 10),
      stock_ml: parseInt(form.stock_ml || "0", 10),
      image_url: form.image_url.trim() || null,
    };
    if (!payload.brand || !payload.name || !payload.price_5ml || !payload.price_10ml) {
      toast.error("Please fill all fields");
      return;
    }
    if (payload.stock_ml < 0) {
      toast.error("Stock cannot be negative");
      return;
    }
    try {
      if (creating) {
        await api.post("/admin/products", payload);
        toast.success("Product added");
      } else if (editingId) {
        await api.put(`/admin/products/${editingId}`, payload);
        toast.success("Product updated");
      }
      cancel();
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.brand} — ${p.name}"?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const onImagePick = (e, target = "form") => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 800_000) {
      toast.error("Image too large. Use <800KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (target === "form") setForm((f) => ({ ...f, image_url: reader.result }));
      else if (target === "hero") setSettings((s) => ({ ...s, hero_image_url: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const [savingIg, setSavingIg] = useState(false);
  const saveInstagram = async () => {
    const url = (settings.instagram_url || "").trim();
    if (url && !/^https?:\/\//i.test(url)) {
      toast.error("Link must start with https://");
      return;
    }
    setSavingIg(true);
    try {
      await api.put("/admin/settings", { instagram_url: url });
      toast.success("Instagram link updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingIg(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {
        whatsapp_number: settings.whatsapp_number,
        instagram_handle: settings.instagram_handle,
        upi_id: settings.upi_id,
        shipping_standard: settings.shipping_standard,
        shipping_reduced: settings.shipping_reduced,
        shipping_threshold_reduced: settings.shipping_threshold_reduced,
        shipping_threshold_free: settings.shipping_threshold_free,
      };
      await api.put("/admin/settings", payload);
      toast.success("Store settings saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingSettings(false);
    }
  };

  const saveContent = async () => {
    setSavingContent(true);
    try {
      const payload = {
        site_name: settings.site_name,
        hero_image_url: settings.hero_image_url,
        hero_eyebrow: settings.hero_eyebrow,
        hero_title: settings.hero_title,
        hero_subtitle: settings.hero_subtitle,
        footer_tagline: settings.footer_tagline,
      };
      await api.put("/admin/settings", payload);
      toast.success("Site content saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingContent(false);
    }
  };

  const [savingTexts, setSavingTexts] = useState(false);
  const saveTexts = async () => {
    setSavingTexts(true);
    try {
      const payload = {
        hero_cta_primary: settings.hero_cta_primary,
        hero_cta_secondary: settings.hero_cta_secondary,
        catalog_eyebrow: settings.catalog_eyebrow,
        catalog_title: settings.catalog_title,
        catalog_empty_text: settings.catalog_empty_text,
        footer_visit_title: settings.footer_visit_title,
        footer_boutique_title: settings.footer_boutique_title,
        footer_bullet_1: settings.footer_bullet_1,
        footer_bullet_2: settings.footer_bullet_2,
        footer_bullet_3: settings.footer_bullet_3,
        footer_bullet_4: settings.footer_bullet_4,
        footer_marquee: settings.footer_marquee,
        about_title: settings.about_title,
        about_teaser: settings.about_teaser,
        about_body: settings.about_body,
      };
      await api.put("/admin/settings", payload);
      toast.success("Site texts saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingTexts(false);
    }
  };

  const savePromo = async () => {
    const pct = parseInt(settings.promo_percent || 0, 10);
    if (settings.promo_active && (!(settings.promo_code || "").trim() || pct <= 0 || pct > 100)) {
      toast.error("Enter a code and a percent between 1 and 100");
      return;
    }
    setSavingPromo(true);
    try {
      const payload = {
        promo_active: !!settings.promo_active,
        promo_code: (settings.promo_code || "").trim().toUpperCase(),
        promo_percent: pct,
        promo_min_subtotal: parseInt(settings.promo_min_subtotal || 0, 10),
      };
      await api.put("/admin/settings", payload);
      setSettings((s) => ({ ...s, promo_code: payload.promo_code }));
      toast.success(payload.promo_active ? "Promo is LIVE" : "Promo saved (off)");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingPromo(false);
    }
  };

  const changePassword = async () => {
    if (!pw.current || !pw.next || !pw.confirm) {
      toast.error("Please fill all password fields");
      return;
    }
    if (pw.next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (pw.next !== pw.confirm) {
      toast.error("New password and confirmation don't match");
      return;
    }
    setChangingPw(true);
    try {
      await api.post("/admin/change-password", {
        current_password: pw.current,
        new_password: pw.next,
      });
      toast.success("Password updated — use the new one next time you log in");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not change password");
    } finally {
      setChangingPw(false);
    }
  };

  const resetAllStock = async () => {
    const ml = parseInt(resetMl || "0", 10);
    if (isNaN(ml) || ml < 0) {
      toast.error("Enter a valid number");
      return;
    }
    if (!window.confirm(`Reset ALL ${products.length} perfumes to ${ml}ml stock each?`)) return;
    setResetting(true);
    try {
      const { data } = await api.post("/admin/products/reset-stock", { stock_ml: ml });
      toast.success(`Reset ${data.updated} products to ${data.stock_ml}ml`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const filtered = products.filter(
    (p) =>
      !q.trim() ||
      p.brand.toLowerCase().includes(q.toLowerCase()) ||
      p.name.toLowerCase().includes(q.toLowerCase())
  );

  const isFormOpen = creating || editingId;

  return (
    <div className="min-h-screen bg-[var(--bg-default)]">
      {/* Admin Header */}
      <div className="border-b border-white/5 bg-[#0A0C11] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--accent-gold)]">Admin</div>
            <div className="font-headings text-lg text-[var(--text-primary)]">{settings.site_name || "tpg.decants"}</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[var(--text-muted)] hidden sm:block">{user?.email}</span>
            <a href="/" className="text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)] hover:text-[var(--brand-gold)]">
              View Site
            </a>
            <button
              data-testid="admin-logout"
              onClick={doLogout}
              className="flex items-center gap-1 px-3 py-1.5 border border-[var(--border-default)] text-xs uppercase tracking-wider text-[var(--text-secondary)] hover:text-red-400 hover:border-red-500/40"
            >
              <LogOut size={14} />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-12">
        {/* Instagram Link (dedicated, separate) */}
        <section className="bg-[var(--bg-surface)] border border-[var(--border-default)] p-6">
          <div className="flex items-center gap-2 mb-1">
            <SettingsIcon size={16} className="text-[var(--brand-gold)]" />
            <h2 className="font-headings text-xl">Instagram Profile Link</h2>
          </div>
          <p className="text-[11px] text-[var(--text-muted)] mb-4">
            This is the link opened when anyone clicks the <strong>Follow on Instagram</strong> button, header @ link, or footer card. Paste the full profile URL (e.g. https://instagram.com/tamil.perfumeguy).
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="setting-instagram-url"
              value={settings.instagram_url || ""}
              onChange={(e) => setSettings({ ...settings, instagram_url: e.target.value })}
              placeholder="https://instagram.com/tamil.perfumeguy"
              className="flex-1 bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
            />
            <button
              data-testid="save-instagram-url"
              onClick={saveInstagram}
              disabled={savingIg}
              className="px-5 py-2 bg-[var(--brand-gold)] text-black text-sm font-medium hover:bg-[var(--brand-gold-hover)] disabled:opacity-60"
            >
              {savingIg ? "Saving…" : "Save Link"}
            </button>
          </div>
        </section>

        {/* Change Password */}
        <section className="bg-[var(--bg-surface)] border border-[var(--border-default)] p-6">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound size={16} className="text-[var(--brand-gold)]" />
            <h2 className="font-headings text-xl">Change Admin Password</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Current Password</label>
              <input
                data-testid="pw-current"
                type="password"
                autoComplete="current-password"
                value={pw.current}
                onChange={(e) => setPw({ ...pw, current: e.target.value })}
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">New Password</label>
              <input
                data-testid="pw-next"
                type="password"
                autoComplete="new-password"
                value={pw.next}
                onChange={(e) => setPw({ ...pw, next: e.target.value })}
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Confirm New Password</label>
              <input
                data-testid="pw-confirm"
                type="password"
                autoComplete="new-password"
                value={pw.confirm}
                onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
            </div>
          </div>
          <p className="text-[11px] text-[var(--text-muted)] mt-2">
            Minimum 8 characters. After changing, you'll log in with the new password next time.
          </p>
          <button
            data-testid="change-password-btn"
            onClick={changePassword}
            disabled={changingPw}
            className="mt-4 px-5 py-2 bg-[var(--brand-gold)] text-black text-sm font-medium hover:bg-[var(--brand-gold-hover)] disabled:opacity-60"
          >
            {changingPw ? "Updating…" : "Update Password"}
          </button>
        </section>

        {/* Site Content */}
        <section className="bg-[var(--bg-surface)] border border-[var(--border-default)] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Palette size={16} className="text-[var(--brand-gold)]" />
            <h2 className="font-headings text-xl">Site Content (Hero, Branding, Footer)</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Site Name (logo)</label>
              <input
                data-testid="content-site-name"
                value={settings.site_name || ""}
                onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                placeholder="tpg.decants"
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
              <p className="text-[11px] text-[var(--text-muted)] mt-1">Use a dot, e.g. <em>tpg.decants</em> — it will be styled gold.</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Hero Eyebrow (small tag above title)</label>
              <input
                data-testid="content-hero-eyebrow"
                value={settings.hero_eyebrow || ""}
                onChange={(e) => setSettings({ ...settings, hero_eyebrow: e.target.value })}
                placeholder="by @tamilperfumeguy_"
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Hero Title (main headline)</label>
              <input
                data-testid="content-hero-title"
                value={settings.hero_title || ""}
                onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })}
                placeholder="A boutique of decanted luxury."
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Hero Subtitle</label>
              <textarea
                data-testid="content-hero-subtitle"
                value={settings.hero_subtitle || ""}
                onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })}
                rows={2}
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm resize-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">
                Cover / Hero Image (upload or paste URL)
              </label>
              <div className="mt-1 flex flex-col sm:flex-row gap-3">
                <input
                  data-testid="content-hero-image-url"
                  value={settings.hero_image_url || ""}
                  onChange={(e) => setSettings({ ...settings, hero_image_url: e.target.value })}
                  placeholder="https://..."
                  className="flex-1 bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2 text-sm"
                />
                <label className="flex items-center justify-center gap-2 px-4 py-2 border border-[var(--border-default)] text-sm text-[var(--text-secondary)] hover:border-[var(--brand-gold)] hover:text-[var(--brand-gold)] cursor-pointer">
                  <ImageIcon size={14} />
                  Upload
                  <input
                    data-testid="content-hero-image-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onImagePick(e, "hero")}
                  />
                </label>
              </div>
              {settings.hero_image_url && (
                <img src={settings.hero_image_url} alt="hero preview" className="mt-3 h-32 w-full object-cover border border-[var(--border-default)]" />
              )}
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Footer Tagline</label>
              <textarea
                data-testid="content-footer-tagline"
                value={settings.footer_tagline || ""}
                onChange={(e) => setSettings({ ...settings, footer_tagline: e.target.value })}
                rows={2}
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm resize-none"
              />
            </div>
          </div>
          <button
            data-testid="save-content"
            onClick={saveContent}
            disabled={savingContent}
            className="mt-4 px-5 py-2 bg-[var(--brand-gold)] text-black text-sm font-medium hover:bg-[var(--brand-gold-hover)] disabled:opacity-60"
          >
            {savingContent ? "Saving…" : "Save Site Content"}
          </button>
        </section>

        {/* Site Texts */}
        <section className="bg-[var(--bg-surface)] border border-[var(--border-default)] p-6">
          <div className="flex items-center gap-2 mb-1">
            <Palette size={16} className="text-[var(--brand-gold)]" />
            <h2 className="font-headings text-xl">Site Texts (Buttons, Labels, Footer)</h2>
          </div>
          <p className="text-[11px] text-[var(--text-muted)] mb-4">
            Edit every piece of text that appears on the site outside the product catalog. Leave a footer bullet blank to hide it.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Hero — Primary Button Label</label>
              <input
                data-testid="text-hero-cta-primary"
                value={settings.hero_cta_primary || ""}
                onChange={(e) => setSettings({ ...settings, hero_cta_primary: e.target.value })}
                placeholder="Explore Catalog"
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Hero — Secondary Button Label</label>
              <input
                data-testid="text-hero-cta-secondary"
                value={settings.hero_cta_secondary || ""}
                onChange={(e) => setSettings({ ...settings, hero_cta_secondary: e.target.value })}
                placeholder="Follow on Instagram"
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
            </div>

            <div className="md:col-span-2 pt-2">
              <div className="text-xs uppercase tracking-[0.25em] text-[var(--accent-gold)] mb-2">Catalog section</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Catalog — Eyebrow</label>
              <input
                data-testid="text-catalog-eyebrow"
                value={settings.catalog_eyebrow || ""}
                onChange={(e) => setSettings({ ...settings, catalog_eyebrow: e.target.value })}
                placeholder="The Catalog"
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Catalog — Headline</label>
              <input
                data-testid="text-catalog-title"
                value={settings.catalog_title || ""}
                onChange={(e) => setSettings({ ...settings, catalog_title: e.target.value })}
                placeholder="Every bottle, in a size that suits you."
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Catalog — Empty State Text</label>
              <input
                data-testid="text-catalog-empty"
                value={settings.catalog_empty_text || ""}
                onChange={(e) => setSettings({ ...settings, catalog_empty_text: e.target.value })}
                placeholder="No perfumes match your search."
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
            </div>

            <div className="md:col-span-2 pt-2">
              <div className="text-xs uppercase tracking-[0.25em] text-[var(--accent-gold)] mb-2">Footer</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Footer — Column 2 Title</label>
              <input
                data-testid="text-footer-visit-title"
                value={settings.footer_visit_title || ""}
                onChange={(e) => setSettings({ ...settings, footer_visit_title: e.target.value })}
                placeholder="Visit the Guy"
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Footer — Column 3 Title</label>
              <input
                data-testid="text-footer-boutique-title"
                value={settings.footer_boutique_title || ""}
                onChange={(e) => setSettings({ ...settings, footer_boutique_title: e.target.value })}
                placeholder="The Boutique"
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
            </div>
            {[1, 2, 3, 4].map((n) => (
              <div key={n}>
                <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Footer — Bullet {n}</label>
                <input
                  data-testid={`text-footer-bullet-${n}`}
                  value={settings[`footer_bullet_${n}`] || ""}
                  onChange={(e) => setSettings({ ...settings, [`footer_bullet_${n}`]: e.target.value })}
                  placeholder={`Bullet ${n}`}
                  className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
                />
              </div>
            ))}
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Footer — Bottom Marquee (right of copyright)</label>
              <input
                data-testid="text-footer-marquee"
                value={settings.footer_marquee || ""}
                onChange={(e) => setSettings({ ...settings, footer_marquee: e.target.value })}
                placeholder="Crafted for the fragrance obsessed"
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
            </div>

            <div className="md:col-span-2 pt-2">
              <div className="text-xs uppercase tracking-[0.25em] text-[var(--accent-gold)] mb-2">About Us (clickable section)</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">About — Section Title</label>
              <input
                data-testid="text-about-title"
                value={settings.about_title || ""}
                onChange={(e) => setSettings({ ...settings, about_title: e.target.value })}
                placeholder="About Us"
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">About — Teaser (shown on the card before expansion)</label>
              <input
                data-testid="text-about-teaser"
                value={settings.about_teaser || ""}
                onChange={(e) => setSettings({ ...settings, about_teaser: e.target.value })}
                placeholder="A story behind every bottle."
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">About — Full Story (revealed on click)</label>
              <textarea
                data-testid="text-about-body"
                value={settings.about_body || ""}
                onChange={(e) => setSettings({ ...settings, about_body: e.target.value })}
                rows={6}
                placeholder="Tell customers your story, mission, or what makes your decants special. Press Enter for new paragraphs."
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm resize-y"
              />
              <p className="text-[11px] text-[var(--text-muted)] mt-1">
                Leave blank to hide the whole About section from the site.
              </p>
            </div>
          </div>
          <button
            data-testid="save-texts"
            onClick={saveTexts}
            disabled={savingTexts}
            className="mt-4 px-5 py-2 bg-[var(--brand-gold)] text-black text-sm font-medium hover:bg-[var(--brand-gold-hover)] disabled:opacity-60"
          >
            {savingTexts ? "Saving…" : "Save Site Texts"}
          </button>
        </section>

        {/* Store Settings */}
        <section className="bg-[var(--bg-surface)] border border-[var(--border-default)] p-6">
          <div className="flex items-center gap-2 mb-4">
            <SettingsIcon size={16} className="text-[var(--brand-gold)]" />
            <h2 className="font-headings text-xl">Store Settings (WhatsApp, UPI, Shipping)</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">WhatsApp Number (with country code, no +)</label>
              <input
                data-testid="setting-whatsapp"
                value={settings.whatsapp_number || ""}
                onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
                placeholder="e.g. 919176431493"
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Instagram Handle (without @)</label>
              <input
                data-testid="setting-instagram"
                value={settings.instagram_handle || ""}
                onChange={(e) => setSettings({ ...settings, instagram_handle: e.target.value })}
                placeholder="tamilperfumeguy_"
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">UPI ID (GPay / PhonePe)</label>
              <input
                data-testid="setting-upi"
                value={settings.upi_id || ""}
                onChange={(e) => setSettings({ ...settings, upi_id: e.target.value })}
                placeholder="sak.2@ybl"
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
            </div>
            <div className="md:col-span-2 pt-2">
              <div className="text-xs uppercase tracking-[0.25em] text-[var(--accent-gold)] mb-2">Shipping Rules</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)]">Default ₹</label>
                  <input data-testid="setting-ship-standard" type="number" value={settings.shipping_standard ?? 100} onChange={(e) => setSettings({ ...settings, shipping_standard: parseInt(e.target.value || "0", 10) })} className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)]">Reduced ₹</label>
                  <input data-testid="setting-ship-reduced" type="number" value={settings.shipping_reduced ?? 50} onChange={(e) => setSettings({ ...settings, shipping_reduced: parseInt(e.target.value || "0", 10) })} className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)]">Reduced ≥</label>
                  <input data-testid="setting-ship-thresh-reduced" type="number" value={settings.shipping_threshold_reduced ?? 1000} onChange={(e) => setSettings({ ...settings, shipping_threshold_reduced: parseInt(e.target.value || "0", 10) })} className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)]">FREE ≥</label>
                  <input data-testid="setting-ship-thresh-free" type="number" value={settings.shipping_threshold_free ?? 3000} onChange={(e) => setSettings({ ...settings, shipping_threshold_free: parseInt(e.target.value || "0", 10) })} className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2 text-sm" />
                </div>
              </div>
              <p className="text-[11px] text-[var(--text-muted)] mt-2">
                Orders under ₹{settings.shipping_threshold_reduced} pay ₹{settings.shipping_standard}, ₹{settings.shipping_threshold_reduced}–₹{(settings.shipping_threshold_free || 0) - 1} pay ₹{settings.shipping_reduced}, ₹{settings.shipping_threshold_free}+ ship FREE.
              </p>
            </div>
          </div>
          <button
            data-testid="save-settings"
            onClick={saveSettings}
            disabled={savingSettings}
            className="mt-4 px-5 py-2 bg-[var(--brand-gold)] text-black text-sm font-medium hover:bg-[var(--brand-gold-hover)] disabled:opacity-60"
          >
            {savingSettings ? "Saving…" : "Save Store Settings"}
          </button>
        </section>

        {/* Promo Code */}
        <section className="bg-[var(--bg-surface)] border border-[var(--border-default)] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Tag size={16} className="text-[var(--brand-gold)]" />
            <h2 className="font-headings text-xl">Promo Code</h2>
          </div>
          <div className="flex items-center justify-between mb-5 p-3 border border-[var(--border-default)]">
            <div>
              <div className="text-sm text-[var(--text-primary)] font-medium">
                {settings.promo_active ? "Promo is LIVE" : "Promo is OFF"}
              </div>
              <div className="text-[11px] text-[var(--text-muted)]">
                Toggle on to let customers apply the code at checkout for a % discount.
              </div>
            </div>
            <button
              data-testid="promo-toggle"
              onClick={() => setSettings({ ...settings, promo_active: !settings.promo_active })}
              role="switch"
              aria-checked={settings.promo_active}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                settings.promo_active ? "bg-[var(--brand-gold)]" : "bg-[var(--border-default)]"
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform ${
                  settings.promo_active ? "translate-x-7" : ""
                }`}
              />
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Promo Code</label>
              <input
                data-testid="promo-code-input"
                value={settings.promo_code || ""}
                onChange={(e) => setSettings({ ...settings, promo_code: e.target.value.toUpperCase() })}
                placeholder="e.g. FIRST10"
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm uppercase tracking-wider"
              />
              <p className="text-[11px] text-[var(--text-muted)] mt-1">Case-insensitive. Letters and numbers only.</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Discount (%)</label>
              <input
                data-testid="promo-percent-input"
                type="number"
                min="1"
                max="100"
                value={settings.promo_percent ?? 10}
                onChange={(e) => setSettings({ ...settings, promo_percent: parseInt(e.target.value || "0", 10) })}
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
              <p className="text-[11px] text-[var(--text-muted)] mt-1">Applied to subtotal before shipping.</p>
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Minimum Order Subtotal (₹)</label>
              <input
                data-testid="promo-min-subtotal-input"
                type="number"
                min="0"
                value={settings.promo_min_subtotal ?? 1000}
                onChange={(e) => setSettings({ ...settings, promo_min_subtotal: parseInt(e.target.value || "0", 10) })}
                className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
              <p className="text-[11px] text-[var(--text-muted)] mt-1">
                The promo field appears in the cart only when subtotal reaches this amount. Set to 0 to always show.
              </p>
            </div>
          </div>
          <button
            data-testid="save-promo"
            onClick={savePromo}
            disabled={savingPromo}
            className="mt-4 px-5 py-2 bg-[var(--brand-gold)] text-black text-sm font-medium hover:bg-[var(--brand-gold-hover)] disabled:opacity-60"
          >
            {savingPromo ? "Saving…" : "Save Promo"}
          </button>
        </section>

        {/* Curated Combos */}
        <CombosAdmin products={products} />

        {/* Bulk stock reset */}
        <section className="bg-[var(--bg-surface)] border border-[var(--border-default)] p-6">
          <div className="flex items-center gap-2 mb-4">
            <RefreshCw size={16} className="text-[var(--brand-gold)]" />
            <h2 className="font-headings text-xl">Bulk Stock Reset</h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Stock (ml) per perfume</label>
              <input
                data-testid="reset-stock-ml"
                type="number"
                min="0"
                value={resetMl}
                onChange={(e) => setResetMl(e.target.value)}
                className="mt-1 w-40 bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
              />
            </div>
            <button
              data-testid="reset-stock-btn"
              onClick={resetAllStock}
              disabled={resetting}
              className="flex items-center gap-2 px-5 py-2.5 bg-[var(--brand-gold)] text-black text-sm font-medium hover:bg-[var(--brand-gold-hover)] disabled:opacity-60"
            >
              <RefreshCw size={14} />
              {resetting ? "Resetting…" : `Reset all ${products.length} perfumes`}
            </button>
            <button
              data-testid="reset-70-btn"
              onClick={() => { setResetMl("70"); }}
              className="text-xs px-3 py-2 border border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--brand-gold)] hover:text-[var(--brand-gold)]"
            >
              Preset: 70ml
            </button>
          </div>
          <p className="text-[11px] text-[var(--text-muted)] mt-2">
            Use this after buying fresh bottles. Every perfume's stock will be set to the value above.
          </p>
        </section>

        {/* Products */}
        <section>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
            <div>
              <h2 className="font-headings text-3xl">Products</h2>
              <p className="text-sm text-[var(--text-secondary)]">
                {products.length} perfumes · {products.filter((p) => p.category === "middle_eastern").length} Middle Eastern · {products.filter((p) => p.category === "premium").length} Premium
              </p>
            </div>
            <div className="flex gap-2">
              <input
                data-testid="admin-search"
                placeholder="Search products…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="flex-1 sm:w-60 bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2 text-sm"
              />
              <button
                data-testid="add-product-btn"
                onClick={startCreate}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--brand-gold)] text-black text-sm font-medium hover:bg-[var(--brand-gold-hover)]"
              >
                <Plus size={16} />
                Add
              </button>
            </div>
          </div>

          {isFormOpen && (
            <div data-testid="product-form" className="bg-[var(--bg-surface)] border border-[var(--brand-gold)]/40 p-5 mb-6 space-y-4">
              <div className="font-headings text-lg">{creating ? "New Product" : "Edit Product"}</div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Brand</label>
                  <input data-testid="form-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Perfume Name</label>
                  <input data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Price 5ml (₹)</label>
                  <input data-testid="form-price-5ml" type="number" value={form.price_5ml} onChange={(e) => setForm({ ...form, price_5ml: e.target.value })} className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Price 10ml (₹)</label>
                  <input data-testid="form-price-10ml" type="number" value={form.price_10ml} onChange={(e) => setForm({ ...form, price_10ml: e.target.value })} className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2 text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Stock (total ml in bottle)</label>
                  <input data-testid="form-stock-ml" type="number" min="0" value={form.stock_ml} onChange={(e) => setForm({ ...form, stock_ml: e.target.value })} className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2 text-sm" />
                  <p className="text-[11px] text-[var(--text-muted)] mt-1">Each 5ml sale reduces this by 5, each 10ml sale reduces it by 10. Sold out when below 5ml.</p>
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Product Image (upload or URL)</label>
                  <div className="mt-1 flex flex-col sm:flex-row gap-3">
                    <input data-testid="form-image-url" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="Image URL or leave blank" className="flex-1 bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2 text-sm" />
                    <label className="flex items-center justify-center gap-2 px-4 py-2 border border-[var(--border-default)] text-sm text-[var(--text-secondary)] hover:border-[var(--brand-gold)] hover:text-[var(--brand-gold)] cursor-pointer">
                      <ImageIcon size={14} />
                      Upload
                      <input data-testid="form-image-upload" type="file" accept="image/*" className="hidden" onChange={(e) => onImagePick(e, "form")} />
                    </label>
                  </div>
                  {form.image_url && (
                    <img src={form.image_url} alt="preview" className="mt-3 h-28 w-28 object-cover border border-[var(--border-default)]" />
                  )}
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button data-testid="form-save" onClick={save} className="flex items-center gap-1 px-5 py-2 bg-[var(--brand-gold)] text-black text-sm font-medium hover:bg-[var(--brand-gold-hover)]">
                  <Save size={14} />
                  Save
                </button>
                <button data-testid="form-cancel" onClick={cancel} className="flex items-center gap-1 px-5 py-2 border border-[var(--border-default)] text-sm text-[var(--text-secondary)] hover:border-[var(--border-hover)]">
                  <X size={14} />
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="overflow-x-auto border border-[var(--border-default)]">
            <table className="w-full text-sm">
              <thead className="bg-[var(--bg-surface)]">
                <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)]">
                  <th className="p-3">Brand</th>
                  <th className="p-3">Name</th>
                  <th className="p-3">Category</th>
                  <th className="p-3">5ml</th>
                  <th className="p-3">10ml</th>
                  <th className="p-3">Stock</th>
                  <th className="p-3">Image</th>
                  <th className="p-3 w-32"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="p-8 text-center text-[var(--text-muted)]">Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={8} className="p-8 text-center text-[var(--text-muted)]">No products found.</td></tr>
                ) : (
                  filtered.map((p) => (
                    <tr key={p.id} data-testid={`admin-row-${p.id}`} className="border-t border-[var(--border-default)] hover:bg-[var(--bg-surface)]/60">
                      <td className="p-3 text-[var(--text-secondary)]">{p.brand}</td>
                      <td className="p-3 font-headings text-base">{p.name}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full border ${
                          p.category === "middle_eastern"
                            ? "bg-[var(--cat-me-bg)] text-[var(--cat-me-text)] border-[var(--cat-me-border)]"
                            : "bg-[var(--cat-pr-bg)] text-[var(--cat-pr-text)] border-[var(--cat-pr-border)]"
                        }`}>
                          {p.category === "middle_eastern" ? "ME" : "Premium"}
                        </span>
                      </td>
                      <td className="p-3 text-[var(--brand-gold)]">{INR(p.price_5ml)}</td>
                      <td className="p-3 text-[var(--brand-gold)]">{INR(p.price_10ml)}</td>
                      <td className="p-3 text-xs">
                        <span className={
                          (p.stock_ml ?? 0) === 0 ? "text-red-400 font-medium"
                          : (p.stock_ml ?? 0) < 5 ? "text-red-400"
                          : (p.stock_ml ?? 0) <= 20 ? "text-amber-300"
                          : "text-[var(--text-secondary)]"
                        }>
                          {p.stock_ml ?? 0}ml
                        </span>
                      </td>
                      <td className="p-3">
                        {p.image_url ? (
                          <img src={p.image_url} alt="" className="h-10 w-10 object-cover border border-[var(--border-default)]" />
                        ) : (
                          <span className="text-xs text-[var(--text-muted)]">—</span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1 justify-end">
                          <button data-testid={`edit-${p.id}`} onClick={() => startEdit(p)} className="p-2 border border-[var(--border-default)] hover:border-[var(--brand-gold)] hover:text-[var(--brand-gold)]">
                            <Pencil size={14} />
                          </button>
                          <button data-testid={`delete-${p.id}`} onClick={() => del(p)} className="p-2 border border-[var(--border-default)] hover:border-red-500/60 hover:text-red-400">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
